import { SuggestionData } from 'ckeditor5-premium-features'
import { useTrackChangesCardStyles } from 'components/WorkspaceEditor/components/TrackChangesPanel/styles/useTrackChangesCardStyles'
import { CardType } from 'components/WorkspaceEditor/components/TrackChangesPanel/types/types'
import {
    TrackChangeStatus,
    TrackChangeType,
    TrackedChange,
} from 'models/changes'
import { useMemo } from 'react'

interface UseCardTypeResult {
    cardType: CardType
    classes: ReturnType<typeof useTrackChangesCardStyles>
}

const SUGGESTION_TYPE_MAP = {
    INSERTION: 'insertion',
    DELETION: 'deletion',
    REPLACED: 'replaced',
    DISABLED: 'disabled',
    MODIFIED: 'modified',
} as const

/**
 * Custom hook to determine the type of a track change card and return associated styles.
 *
 * @param {SuggestionData} cardSuggestion - The suggestion data for the card.
 * @param {boolean} isNotDefaultSuggestionCardFilter - Flag to indicate if the default suggestion card filter is not applied.
 * @param {TrackedChange} [trackChanges] - Optional tracked change data.
 * @returns {UseCardTypeResult} An object containing the card type and associated styles.
 */
export const useCardType = (
    cardSuggestion: SuggestionData,
    isNotDefaultSuggestionCardFilter: boolean,
    trackChanges?: TrackedChange
): UseCardTypeResult => {
    // Combine related calculations into a single useMemo for cardType and derived values
    const { cardType, isAddedCardType } = useMemo(() => {
        const suggestionType = cardSuggestion?.type?.toLowerCase() || ''
        const changeType = trackChanges?.changeType
        const trackChangeStatus = trackChanges?.status?.toUpperCase()
        const isApproved = trackChangeStatus === TrackChangeStatus.APPROVED
        const isRejected = trackChangeStatus === TrackChangeStatus.REJECTED

        const cardType: CardType = {
            isAdded: suggestionType === SUGGESTION_TYPE_MAP.INSERTION,
            isReplaced: suggestionType === SUGGESTION_TYPE_MAP.REPLACED,
            isDeleted: suggestionType === SUGGESTION_TYPE_MAP.DELETION,
            isApprovedAdded: isApproved && changeType === TrackChangeType.ADDED,
            isApprovedDeleted:
                isApproved && changeType === TrackChangeType.DELETED,
            isRejectedAdded: isRejected && changeType === TrackChangeType.ADDED,
            isRejectedDeleted:
                isRejected && changeType === TrackChangeType.DELETED,
            isDisabled: suggestionType === SUGGESTION_TYPE_MAP.DISABLED,
            isModified: suggestionType === SUGGESTION_TYPE_MAP.MODIFIED,
        }

        // Calculate derived value
        const isAddedCardType =
            cardType.isAdded ||
            cardType.isApprovedAdded ||
            cardType.isRejectedAdded ||
            cardType.isModified

        return { cardType, isAddedCardType }
    }, [cardSuggestion?.type, trackChanges?.changeType, trackChanges?.status])

    // Get styles with proper dependencies
    const classes = useTrackChangesCardStyles(
        useMemo(
            () => ({
                isAdded: isAddedCardType,
                isNotDefaultSuggestionCardFilter,
            }),
            [isAddedCardType, isNotDefaultSuggestionCardFilter]
        )
    )

    return { cardType, classes }
}
